import React, { useContext, useState, useEffect, useLayoutEffect, useRef } from "react";
import { makeArray, filterReadFields } from "../../permissionUtils";
import LayerPopup from "../SideBar/Results/LayerPopup";
import { useSelector, useDispatch } from "react-redux";
import { getFeatureWithId } from "../utils/mapUtils";
import { TabStrip, TabStripTab } from "@progress/kendo-react-layout";
import {
    formatFieldValue,
    parseFeatureDescription,
} from "../SideBar/Results/Identify/FeatureDescription";
import MapContext from "../MapContext";
import { setDataLinkData } from "../../redux/resultsSlice";
import { dataAPI } from "../../api/axios";
import DataLinksGrid from "../DataLinks/DataLinksGrid";
import { FaFilter } from "react-icons/fa";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import VectorSource from "ol/source/Vector.js";
import VectorLayer from "ol/layer/Vector";
import { inAndOut } from "ol/easing";
import Stroke from "ol/style/Stroke.js";
import Style from "ol/style/Style.js";

const HorizontalFeature = () => {
    const { map } = useContext(MapContext);
    const [layer] = useSelector((state) => state.resultsData.resultsForFeature);
    const [selectedTab, setSelectedTab] = useState(0);
    const [tabs, setTabs] = useState(["Description", "Details"]);
    const [filterOn, setFilterOn] = useState(false);
    const _excelExporter = useRef();
    const exportDataRef = useRef();
    const dispatch = useDispatch();
    const vectorLayer = useRef();
    const layerId = layer.Id;
    const featureId = layer.Features[0];

    /* --- Permission Variables --- */

    const permissions = useSelector((state) => state.configData.mapConfig.Permissions);

    const sitePermissionRoles = makeArray(permissions).map((p) => p.Value);

    const fieldPermissions = useSelector(
        (state) => state.authorizedPermissions.fieldPermissions
    );

    /* --- Variables for feature on map --- */

    //Find config data for selected layer from state
    const vectorLayerProperties = useSelector((state) =>
        state.configData.vectorLayerProperties.find((l) => l.Id === layer.Id)
    );

    // get feature from map
    const feature = getFeatureWithId(map, layer.Id, layer.Features[0]);

    /* --- Feature fields variabels --- */

    // Get fields from vector
    const vectorFields = vectorLayerProperties.Fields
        ? makeArray(vectorLayerProperties.Fields.Field)
        : [{ Name: vectorLayerProperties.DisplayName }];

    // Filter vector fields by readable fields
    const readFields = filterReadFields(vectorFields, fieldPermissions, layer.Id);
    if (readFields[0].DisplayIndex !== undefined) {
        readFields.sort((a, b) => (a.DisplayIndex > b.DisplayIndex ? 1 : -1));
    }

    // Create description
    const description = parseFeatureDescription(vectorLayerProperties, feature.values_);

    /* --- DataLink Variables --- */

    // Results from API call
    const dataLinkData = useSelector((state) => state.resultsData.dataLinkData);

    // Data link info from layer layer config
    const dataLinksConfig = vectorLayerProperties?.DataLinks?.DataLink
        ? makeArray(vectorLayerProperties?.DataLinks?.DataLink)
        : [];

    // Specific dataLink
    const dataLinkConfig = dataLinksConfig?.find(
        (d) => d.DisplayName === tabs[selectedTab]
    );

    // Filter data links by permissions
    const permissedDataLinks = dataLinksPermissionsFilter(
        dataLinksConfig,
        sitePermissionRoles
    );

    /* --- Event Handlers --- */

    const excelExport = () => {
        if (_excelExporter.current) {
            if (tabs[selectedTab] === "Details") {
                const dataObj = {};
                readFields.forEach((f) => {
                    Object.assign(dataObj, { [f.Name]: feature.values_[f.Name] });
                });
                exportDataRef.current = [dataObj];
            } else if (tabs[selectedTab] !== "Description") {
                exportDataRef.current = dataLinkData.find(
                    (d) => d.Id === dataLinkConfig.Id
                );
            }
            const columns = Object.keys(exportDataRef.current[0]).map((d) => {
                return { field: d };
            });
            _excelExporter.current.save(exportDataRef.current, columns);
        }
    };

    const handleTabSelect = (e) => {
        setSelectedTab(e.selected);
    };

    /* --- Data Getters --- */

    const getDataLinkData = () => {
        fetchDataLinks(layerId, featureId, permissedDataLinks).then((data) => {
            dispatch(
                setDataLinkData({
                    id: layer.Features[0],
                    data: data,
                })
            );
        });
    };

    /* --- Effects --- */

    // Call API for datalinks
    useEffect(() => {
        if (permissedDataLinks.length > 0) {
            getDataLinkData();
        }
        return () => {
            dispatch(setDataLinkData({}));
        };
    }, [layer]);

    // Update tabs before painting DOM IF there are datalinks
    useLayoutEffect(() => {
        if (dataLinksConfig) {
            const newTabs = ["Description", "Details"];
            permissedDataLinks?.forEach((d) => newTabs.push(d?.DisplayName));
            setTabs(newTabs);
        }
    }, [layer]);

    // Draw and undraw feature
    useEffect(() => {
        const extent = feature.getGeometry().getExtent();
        map.getView().fit(extent, {
            padding: [60, 60, 60, 60],
            duration: 750,
            easing: inAndOut,
            minResolution: 1,
        });
        vectorLayer.current = new VectorLayer({
            source: new VectorSource(),
            map: map,
            style: highlightStyle,
            zIndex: 100,
        });
        vectorLayer.current.getSource().addFeature(feature);
        return () => {
            vectorLayer.current.getSource().clear();
        };
    }, [layer]);

    return (
        <div className="grid-panel-top horizontal-view-wrapper">
            <div className="grid-toolbar">
                <div className="grid-title">{layer.DisplayName}</div>
                <div className="grid-options">
                    {!["Details", "Description"].includes(tabs[selectedTab]) && (
                        <nav
                            className={`grid-option ${filterOn ? "selected" : ""}`}
                            title="Filter">
                            <FaFilter
                                onClick={() => setFilterOn(!filterOn)}
                                style={{
                                    paddingRight: "5px",
                                    display: "flex",
                                    width: "15px",
                                }}
                            />
                        </nav>
                    )}
                    {tabs[selectedTab] !== "Description" && (
                        <nav
                            className="grid-option"
                            onClick={excelExport}
                            title="Export to Excel">
                            <span
                                className="k-icon k-i-file-excel"
                                style={{ paddingRight: "5px" }}></span>
                        </nav>
                    )}
                    <LayerPopup feature={feature} layerId={layer.Id} />
                </div>
            </div>
            {tabs && (
                <>
                    <TabStrip selected={selectedTab} onSelect={handleTabSelect}>
                        {tabs.map((tab) => {
                            return <TabStripTab title={tab} key={tab} />;
                        })}
                    </TabStrip>
                    <div className="horizontal-content">
                        {tabs[selectedTab] === "Description" ? (
                            <div>{description}</div>
                        ) : tabs[selectedTab] === "Details" ? (
                            <DetailsColumns fields={readFields} feature={feature} />
                        ) : (
                            <DataLinksGrid
                                dataLinkConfig={dataLinkConfig}
                                filterOn={filterOn}
                            />
                        )}
                    </div>
                </>
            )}
            {tabs && <ExcelExport ref={_excelExporter} fileName={tabs[selectedTab]} />}
        </div>
    );
};

export default HorizontalFeature;

export const DetailsColumns = ({ fields, feature }) => {
    const visibleFields = fields.filter((f) => f.Visible);
    const length = visibleFields.length;
    let columnOne;
    let columnTwo;
    let columnThree;
    if (length > 8) {
        const size = Math.round(length / 3);
        columnOne = visibleFields.slice(0, size);
        columnTwo = visibleFields.slice(size, size + size);
        columnThree = visibleFields.slice(size + size);
    } else {
        columnOne = visibleFields;
    }

    const DetailsColumn = ({ column }) => {
        return column.map((c) => {
            const displayValue = formatFieldValue(c, feature.values_);
            return (
                <div key={c.Name}>
                    <div className="results-field">{c.DisplayName ?? c.Name}</div>
                    <div className="results-field-value">{displayValue}</div>
                </div>
            );
        });
    };

    return (
        <div className="details-columns">
            <div className="column">
                <DetailsColumn column={columnOne} />
            </div>
            <div className="column column-plus">
                {columnTwo && <DetailsColumn column={columnTwo} />}
            </div>
            <div className="column column-plus">
                {columnThree && <DetailsColumn column={columnThree} />}
            </div>
        </div>
    );
};

/**
 *
 * @param {string} layerId Id for layer with datalinks
 * @param {string} featureId Id for specific feature (number as string)
 * @param {Array<object>} permissedDataLinks Datalink configuration
 * @returns {object | "No Data"} Object with each property representing a datalink
 *  in format {DataLinkID: [data]}. If response has no data "No Data" is returned
 */

const fetchDataLinks = async (layerId, featureId, permissedDataLinks) => {
    const res = await dataAPI.get(`/Layers/${layerId}/Data/${featureId}/DataLink`);
    const validLinkIds = permissedDataLinks.map((d) => d.DataLinkID);
    const data = {};
    validLinkIds.forEach((l) => {
        if (res?.data[l]) {
            Object.assign(data, { [l]: res.data[l] });
        }
    });
    return Object.keys(data).length > 0 ? data : "No Data";
};

// Show data links based on permissions
const dataLinksPermissionsFilter = (dataLinksConfig, permissions) => {
    if (!dataLinksConfig) return;
    return dataLinksConfig.filter((d) => {
        const hasFalse = makeArray(d?.Permissions?.Permission).some(
            (l) => permissions.includes(l?.Value) && l?.Read === false
        );
        return !hasFalse;
    });
};

const highlightStyle = new Style({
    zIndex: 10,
    stroke: new Stroke({
        color: "blue",
        width: 2,
    }),
});
